import {
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Button } from "@heroui/react";
import { moveItem } from "../../lib/profilesDoc";
import { Icon } from "../../ui/icons";
import { useStaffText } from "../../ui/StaffText";
import { MoveButtons } from "../recordParts";

type Item<T extends string> = { id: T; label: ReactNode; name?: string };

/**
 * The picked items as a list, the rest as Add chips under it. Ordered: numbered,
 * with up/down and a drag grip; unordered: a plain subset, kept in `all`'s order.
 */
export function OrderedPick<T extends string>({
  all,
  picked,
  onChange,
  readOnly = false,
  ordered = true,
  empty,
}: {
  all: Item<T>[];
  picked: T[];
  onChange: (next: T[]) => void;
  readOnly?: boolean;
  ordered?: boolean;
  /** Shown in place of the list while nothing is picked. */
  empty?: ReactNode;
}) {
  const { say } = useStaffText();
  const rows = useRef<(HTMLLIElement | null)[]>([]);
  const [drag, setDrag] = useState<{ from: number; over: number } | null>(null);
  const byId = new Map(all.map((it) => [it.id, it]));
  const nameOf = (id: T) => byId.get(id)?.name ?? String(id);
  const rest = all.filter((it) => !picked.includes(it.id));

  const add = (id: T) => {
    if (ordered) return onChange([...picked, id]);
    const next = new Set([...picked, id]);
    onChange(all.map((it) => it.id).filter((x) => next.has(x)));
  };

  const overAt = (y: number) => {
    let at = 0;
    rows.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (y > r.top + r.height / 2) at = i;
    });
    return Math.min(at, picked.length - 1);
  };
  const grip = (i: number) => ({
    onPointerDown: (e: ReactPointerEvent<HTMLSpanElement>) => {
      if (readOnly || e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDrag({ from: i, over: i });
    },
    onPointerMove: (e: ReactPointerEvent<HTMLSpanElement>) => {
      if (!drag) return;
      const over = overAt(e.clientY);
      if (over !== drag.over) setDrag({ ...drag, over });
    },
    onPointerUp: () => {
      if (!drag) return;
      if (drag.from !== drag.over)
        onChange(moveItem(picked, drag.from, drag.over));
      setDrag(null);
    },
    onPointerCancel: () => setDrag(null),
  });

  return (
    <div className="pf-pick">
      {picked.length > 0 ? (
        <ol className="picklist">
          {picked.map((id, i) => {
            const cls = ["picklist__row", "pf-pick__row"];
            if (drag?.from === i) cls.push("is-dragged");
            if (drag && drag.over === i && drag.from !== i)
              cls.push(drag.from < i ? "is-below" : "is-above");
            return (
              <li
                key={id}
                ref={(el) => {
                  rows.current[i] = el;
                }}
                className={cls.join(" ")}
              >
                {ordered && !readOnly && (
                  <span
                    className="pf-pick__grip"
                    aria-hidden="true"
                    {...grip(i)}
                  >
                    <Icon name="drag" size={16} />
                  </span>
                )}
                {ordered && <span className="picklist__n">{i + 1}</span>}
                <span className="picklist__name pf-pick__label">
                  {byId.get(id)?.label ?? id}
                </span>
                {ordered && !readOnly && (
                  <MoveButtons
                    name={nameOf(id)}
                    first={i === 0}
                    last={i === picked.length - 1}
                    onMove={(by) => onChange(moveItem(picked, i, i + by))}
                  />
                )}
                {!readOnly && (
                  <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    aria-label={say("profiles.wp.removeItem", {
                      name: nameOf(id),
                    })}
                    onPress={() => onChange(picked.filter((x) => x !== id))}
                  >
                    <Icon name="close" size={16} />
                  </Button>
                )}
              </li>
            );
          })}
        </ol>
      ) : (
        empty && <p className="picklist pf-pick__empty">{empty}</p>
      )}
      {!readOnly && rest.length > 0 && (
        <div className="picklist__add">
          {rest.map((it) => (
            <Button
              key={it.id}
              variant="outline"
              size="sm"
              onPress={() => add(it.id)}
            >
              <Icon name="plus" size={14} /> {it.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
